import { useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import {
  Annotation,
  GET_ORGANIZATION_ANNOTATIONS_PATH,
  GetOrganizationAnnotationsErrorDto,
  GetOrganizationAnnotationsSuccessDto,
} from '@/api/rest/resources/types/conversation';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { assertIsDefined } from '@/utils/assert';

export const useOrganizationAnnotations = (filters?: Record<string, string>, organizationIdArg?: string) => {
  const orgId = useOrganizationId();
  const organizationId = organizationIdArg ?? orgId;

  assertIsDefined(organizationId, 'organizationId');

  const pathVariables = { organization_id: organizationId };
  const queryParams = filters ?? undefined;

  const queryFn = () => {
    return getRequest<GetOrganizationAnnotationsSuccessDto>(
      GET_ORGANIZATION_ANNOTATIONS_PATH,
      pathVariables,
      queryParams,
    );
  };

  return useSuspenseQuery<GetOrganizationAnnotationsSuccessDto, GetOrganizationAnnotationsErrorDto>({
    queryKey: ['organization', pathVariables, 'annotations', queryParams],
    queryFn,
    staleTime: 1000 * 60 * 2,
    select: (data) => data,
  });
};

export type AnnotationsByMessageId = Record<string, Annotation[]>;

export const groupAnnotationsByMessage = (annotations: Annotation[]): AnnotationsByMessageId =>
  annotations.reduce<AnnotationsByMessageId>((acc, newAnnotation) => {
    const annotationsOnMessage = acc[newAnnotation.message.id] ?? [];
    return { ...acc, [newAnnotation.message.id]: [...annotationsOnMessage, newAnnotation] };
  }, {});
